var _process_env;
import { getAllCollections } from '../core/collection';
/**
 * Every mutation declared in the app. The ledger test walks this: a new
 * `defineMutation` that nobody has written sync/rollback coverage for fails
 * the build, which is the only reliable way to keep the guarantee from
 * decaying one hurried PR at a time.
 */ const registry = new Map();
export const listMutations = ()=>[
        ...registry.keys()
    ].sort();
/** Test-only: forget every declaration (module registries outlive a suite). */ export const __resetMutationRegistry = ()=>{
    registry.clear();
};
/**
 * Dev-only contract breaches, newest last. Populated by the reporter below and
 * asserted on in tests; in production the reporter never runs.
 */ const violations = [];
export const getMutationViolations = ()=>violations;
export const clearMutationViolations = ()=>{
    violations.length = 0;
};
let reportViolations = typeof process === 'undefined' || (process === null || process === void 0 ? void 0 : (_process_env = process.env) === null || _process_env === void 0 ? void 0 : _process_env.NODE_ENV) !== 'production';
/** Test-only: force the reporter on/off regardless of `NODE_ENV`. */ export const setMutationReporting = (enabled)=>{
    reportViolations = enabled;
};
const report = (violation)=>{
    violations.push(violation);
    // eslint-disable-next-line no-console
    console.error(`[state-db] ${violation.mutation}: ${violation.kind} — ${violation.detail}`);
};
/**
 * Watches every collection for the duration of a mutation's optimistic window
 * and reports the two mistakes that are otherwise invisible:
 *
 *  1. A companion patch broadcast BEFORE the request persisted. The peer
 *     applies a value this tab can still roll back, and nothing retracts it
 *     over there — the phantom is permanent.
 *  2. A write to a collection the mutation never declared. That write has no
 *     undo registered, so a failure leaves it stranded.
 *
 * The first check does not need to know which collection the mutation "owns".
 * A committing entity emits `origin: 'mutation'`; only a hand-written
 * companion reaches the synced store via `push`. So `origin: 'push'` carrying
 * `source: 'mutation'` inside the optimistic window is, by construction, a
 * companion broadcasting early.
 *
 * Dev-time only, and nothing here throws: reporting a contract breach must not
 * take down the flow the user is in the middle of.
 */ const watchWrites = (mutationName, declared)=>{
    if (!reportViolations) {
        return ()=>undefined;
    }
    const stops = getAllCollections().map((collection)=>collection.subscribeToWrites((event)=>{
            if (!declared.has(collection.id)) {
                report({
                    mutation: mutationName,
                    kind: 'undeclared-collection',
                    detail: `wrote to '${collection.id}', which is not in sync.entities. ` + `Add it, or route the write through a declared collection — an ` + `undeclared write has no undo and survives a rollback.`
                });
                return;
            }
            if (event.origin === 'push' && event.source === 'mutation') {
                report({
                    mutation: mutationName,
                    kind: 'broadcast-before-commit',
                    detail: `pushed to '${collection.id}' with source: 'mutation' during the ` + `optimistic window. A companion patch is this tab's guess — use ` + `source: 'local' and let sync.signals converge the peers, or they ` + `keep a value this tab can still roll back.`
                });
            }
        }));
    return ()=>stops.forEach((fn)=>fn());
};
/** Runs undos newest-first, so patches layered on each other unwind cleanly. */ const runUndos = (mutationName, undos)=>{
    for(let i = undos.length - 1; i >= 0; i -= 1){
        try {
            undos[i]();
        } catch (error) {
            // A throwing undo must not stop the remaining ones — a half-unwound
            // rollback is strictly worse than a fully-attempted one.
            if (reportViolations) {
                report({
                    mutation: mutationName,
                    kind: 'undo-threw',
                    detail: `an undo threw during rollback: ${String(error)}`
                });
            }
        }
    }
};
/**
 * Declare a mutation, forcing its author to answer the two questions that keep
 * getting skipped: **what does the user see immediately, and how is that
 * undone**, and **what do the other tabs need to hear**.
 *
 * Neither `optimistic` nor `sync` has a default. That is deliberate. Both
 * failure modes — a write that waits on the round-trip, and a change that
 * never reaches the peer — are silent: nothing errors, nothing logs, the flow
 * merely feels slow or goes stale, and it takes a bug report to find. A
 * required field turns each into a compile error where the mutation is
 * written.
 *
 * Execution order:
 *
 * 1. `prepare` captures pre-mutation state (source phase, old value, …).
 * 2. `optimistic.companions` apply, each returning its own undo. They run
 *    before the entity so a patch that throws costs nothing — no request has
 *    been sent yet.
 * 3. `optimistic.entity` applies the overlay and starts the request.
 * 4. On failure — companion undos run newest-first, then the error rethrows.
 *    The entity's own overlay is reverted by its `Transaction`.
 * 5. On success — `reconcile` settles the value, then `sync.signals` fire.
 *    Never earlier: a peer told to re-read while the server still reports the
 *    old state reads the old state and puts the change straight back.
 */ export function defineMutation(def) {
    const { name, optimistic, sync } = def;
    // Resolved on first run, not at declaration: `defineMutation` is called at
    // module scope, and reading a collection from a module that is still
    // evaluating (import cycles are the norm once a mutation spans two
    // collections) yields `undefined`. Memoized — the set never changes.
    let declared;
    const declaredIds = ()=>{
        if (!declared) {
            const entities = typeof sync.entities === 'function' ? sync.entities() : sync.entities;
            declared = new Set(entities.map((c)=>c.id));
        }
        return declared;
    };
    const run = async (args)=>{
        var _def_prepare, _optimistic_result;
        const prepared = (_def_prepare = def.prepare) === null || _def_prepare === void 0 ? void 0 : _def_prepare.call(def, args);
        const stopWatching = watchWrites(name, declaredIds());
        const undos = [];
        try {
            var _ref;
            var _optimistic_companions;
            const patches = (_ref = (_optimistic_companions = optimistic.companions) === null || _optimistic_companions === void 0 ? void 0 : _optimistic_companions.call(optimistic, args, prepared)) !== null && _ref !== void 0 ? _ref : [];
            for (const p of patches){
                // Collected one at a time: if patch 3 throws, the undos for 1 and 2
                // are already recorded and the catch below unwinds them.
                undos.push(p.apply());
            }
            const tx = optimistic.entity(args, prepared);
            await tx.isPersisted;
        } catch (error) {
            runUndos(name, undos);
            stopWatching();
            throw error;
        }
        stopWatching();
        if (typeof optimistic.reconcile === 'object' && optimistic.reconcile.refetch) {
            await optimistic.reconcile.refetch();
        }
        if (sync.signals !== 'none') {
            const signals = sync.signals(args, prepared);
            if (reportViolations && signals.length === 0 && undos.length > 0) {
                report({
                    mutation: name,
                    kind: 'companions-without-signal',
                    detail: `applied ${undos.length} companion patch(es) but sent no signal. ` + `Those patches exist because some surface renders this change from ` + `something other than the entity — the peer renders it the same way ` + `and will not update. Declare a signal, or sync.signals: 'none' ` + `with a reason.`
                });
            }
            signals.forEach((signal)=>signal.send());
        }
        return (_optimistic_result = optimistic.result) === null || _optimistic_result === void 0 ? void 0 : _optimistic_result.call(optimistic, args, prepared);
    };
    const mutation = {
        name,
        run
    };
    registry.set(name, mutation);
    return mutation;
}
